/**
 * Bidirectional scan reconciliation engine (b17a).
 *
 * Today the live scan is purely additive: it marks caught entries it reads and
 * never disagrees with the user. b17a makes it bidirectional — it ALSO compares
 * the scan against the user's manual Dex and surfaces GAPS: cases where the scan
 * confidently read a cell as NOT caught while the user has manually marked it
 * caught. Surfacing the gap helps the user keep their Dex an accurate mirror, so
 * event priorities and trade matches stay correct.
 *
 * This module is the PURE decision layer. It takes the per-(variant,dexNumber)
 * "not caught" readings the session collected, the current store state, and the
 * user's settings, and returns the set of conflicts to surface. It never mutates
 * the store, never touches the camera, and never throws — so the policy table
 * below is fully unit-testable.
 *
 * ── Policy (Option C hybrid) ────────────────────────────────────────────────
 *
 *   | Scan result | Nexus state | OCR confidence | Action                     |
 *   |-------------|-------------|----------------|----------------------------|
 *   | Caught      | Not caught  | Any            | Silent apply (additive)    |
 *   | Caught      | Caught      | Any            | No-op                      |
 *   | Not caught  | Caught      | HIGH           | Conflict (surface to user) |
 *   | Not caught  | Caught      | Medium/Low     | No-op (don't risk a wipe)  |
 *   | Not caught  | Not caught  | Any            | No-op                      |
 *
 * The additive "caught" rows are handled by the existing apply path
 * (`applyScanObservations`) — this module only computes the bottom-half
 * conflict rows. We NEVER mutate caught→not-caught automatically; a conflict is
 * a SUGGESTION the user confirms or dismisses.
 *
 * ── Confidence ──────────────────────────────────────────────────────────────
 *
 * Confidence flows through the single shared helper [[dexScanConfidence]]; the
 * HIGH bar is profile-dependent (Standard / Conservative / Aggressive). This is
 * the only gate between an OCR misfire and a wrongly-surfaced gap, which is why
 * small-sprite false negatives (b16's calibration target) must clear the HIGH
 * bar before they can ever suggest un-catching.
 */

import { getDexEntryState } from "./dexStateStore";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { speciesName } from "../data/speciesNames";
import {
  isHighConfidence,
  type ConfidenceProfile,
} from "./dexScanConfidence";

/**
 * A single "this cell read as NOT caught" observation the session collected
 * during a scan, with the confidence of the frame it was read on.
 */
export interface NotCaughtReading {
  variant: PokedexVariant;
  dexNumber: number;
  /** Frame OCR confidence (0-100) of the read this was seen on. */
  confidence: number;
}

/**
 * A surfaced conflict: the scan confidently says "not caught" but the user has
 * a manual "caught" mark. Carries everything the UI needs to render the toast /
 * review card and to act on a confirmation.
 */
export interface DexReconciliationConflict {
  variant: PokedexVariant;
  dexNumber: number;
  /** Display name, e.g. "Croconaw". */
  speciesName: string;
  /** Best (highest) confidence across the readings for this entry. */
  confidence: number;
}

export interface ReconcileOptions {
  /** Sensitivity profile controlling the HIGH-confidence cutoff. */
  profile: ConfidenceProfile;
  /**
   * Reader for the current Dex state. Injectable so the engine is pure and
   * testable; defaults to the real `dexStateStore` reader.
   */
  readState?: typeof getDexEntryState;
}

/**
 * Compute the conflicts to surface from a batch of "not caught" readings.
 *
 * For each distinct (variant, dexNumber):
 *   1. Keep the highest-confidence reading.
 *   2. Skip it unless that confidence clears the HIGH bar for the profile —
 *      Medium/Low never suggests an un-catch (the "don't risk wiping legit
 *      data" rule).
 *   3. Only surface it when the user's CURRENT state for that entry is a
 *      "present" mark they OWN — i.e. `source === "manual"`. A scan-sourced
 *      "present" disagreeing with a later "not caught" is just OCR noise
 *      between frames, not a manual fact worth second-guessing.
 *
 * Returns conflicts sorted by dexNumber for a stable review list. Never throws.
 */
export function reconcileNotCaughtReadings(
  readings: NotCaughtReading[],
  opts: ReconcileOptions,
): DexReconciliationConflict[] {
  const readState = opts.readState ?? getDexEntryState;

  // Collapse to the best-confidence reading per (variant, dexNumber).
  const best = new Map<string, NotCaughtReading>();
  for (const r of readings) {
    if (!Number.isFinite(r.dexNumber) || r.dexNumber < 1) continue;
    const key = `${r.variant}:${r.dexNumber}`;
    const prev = best.get(key);
    if (!prev || r.confidence > prev.confidence) best.set(key, r);
  }

  const conflicts: DexReconciliationConflict[] = [];
  best.forEach((r) => {
    // Medium/Low confidence "not caught" must never suggest an un-catch.
    if (!isHighConfidence(r.confidence, opts.profile)) return;
    const cur = readState(r.variant, r.dexNumber);
    // Only a manually-owned "caught" mark is worth surfacing a conflict over.
    if (!cur || cur.state !== "present" || cur.source !== "manual") return;
    conflicts.push({
      variant: r.variant,
      dexNumber: r.dexNumber,
      speciesName: speciesName(r.dexNumber),
      confidence: r.confidence,
    });
  });

  conflicts.sort((a, b) => a.dexNumber - b.dexNumber);
  return conflicts;
}

/**
 * UI routing for a batch of conflicts, per the spec's Conflict UX:
 *   - 0        → nothing to show
 *   - 1-2      → inline toast(s)
 *   - 3+       → summary card ("Nexus spotted X possible gaps…")
 */
export type ConflictPresentation = "none" | "toast" | "summary";

export function conflictPresentation(count: number): ConflictPresentation {
  if (count <= 0) return "none";
  if (count <= 2) return "toast";
  return "summary";
}
